// ---------------------------------------------------------------------------------------------------------------------
// MP CUSTOM ELEMENTS
// ---------------------------------------------------------------------------------------------------------------------
// Custom web components migrated from the Mobile Pixels theme to back the `mp-figure`, `mp-links` and `mp-product-list`
// sections. Kept in a standalone module so the bundled `theme.js` is never touched (survives theme upgrades).
// Dependencies are resolved through the theme's importmap (see layout/theme.liquid): "vendor" (Motion) and "luxon".
//
//  * countdown-timer  - used by the `mp-figure-timer` snippet (depends on luxon for timezone-aware parsing)
//  * mp-product-list  - scroll-reveal animation for the `mp-product-list` section (depends on Motion from vendor)
//  * mp-countdown     - banner countdown bar for the `mp-countdown` section (depends on luxon, LA-timezone based)
//
// Note: `<mp-figure>` is purely CSS-styled in the source theme (its define is commented out), so it needs no JS here.

import { inView, timeline } from "vendor";
import { DateTime } from "luxon";

window.DateTime = DateTime;

var CountdownTimer = class extends HTMLElement {
  constructor() {
    super();
    this._interval = null;
    this._text = this.querySelector('template').content.querySelector('countdown-text').innerHTML;
    this._end = this.getAttribute("end") ? DateTime.fromFormat(this.getAttribute("end") + " America/Los_Angeles", 'yyyy-MM-dd HH:mm:ss z').toJSDate() : null;
    this._show = this.getAttribute("show") ? DateTime.fromFormat(this.getAttribute("show") + " America/Los_Angeles", 'yyyy-MM-dd HH:mm:ss z').toJSDate() : null;
  }
  connectedCallback() {
    if (!this._isValidDate(this._end) || !this._text) {
      return this.disconnectedCallback();
    }

    if (this._isValidDate(this._show) && new Date() < this._show) {
      return this.disconnectedCallback();
    }
    this._interval = setInterval(this._update.bind(this), 1e3);
    this._update();
  }
  _update() {
    const now = new Date();
    if (now >= this._end) {
      if (this.parentNode.parentNode.classList.contains('offer')) {
        this.parentNode.parentNode.remove();
      } else {
        this.parentNode.remove();
      }
      this.disconnectedCallback();
    } else {
      this.innerHTML = this._text.replace('{$TIME}', this._formatDiff(this._end - now));
    }
  }
  _isValidDate(d) {
    return d instanceof Date && !isNaN(d.getTime());
  }
  _formatDiff(diff) {
    const formatNumber = (num) => {
      return num < 10 ? `0${num}` : num;
    };
    const days = formatNumber(Math.floor(diff / (1000 * 60 * 60 * 24)));
    const hours = formatNumber(Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    const minutes = formatNumber(Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)));
    const seconds = formatNumber(Math.floor((diff % (1000 * 60)) / 1000));
    return `${days}<sub>D</sub> : ${hours}<sub>H</sub> : ${minutes}<sub>M</sub> : ${seconds}<sub>S</sub>`;
  }
  disconnectedCallback() {
    this._interval && clearInterval(this._interval);
  }
};

if (!window.customElements.get("countdown-timer")) {
  window.customElements.define("countdown-timer", CountdownTimer);
}

var MPProductList = class extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      inView(this, this._reveal.bind(this), { margin: "-100px" });
    }
  }
  _reveal() {
    const toReveal = Array.from(this.querySelectorAll("[reveal-js]"));
    timeline([
      [toReveal, { opacity: 1 }, { duration: 0 }],
      [toReveal.map((item) => item.querySelector("img, svg")), { opacity: [0, 1], transform: ["scale(1.03)", "auto"] }, { duration: 0.2 }],
      [toReveal.map((item) => item.querySelector(".mp-product-card__info")), { opacity: [0, 1] }, { duration: 0.2 }]
    ]);
  }
};

if (!window.customElements.get("mp-product-list")) {
  window.customElements.define("mp-product-list", MPProductList);
}

// ---------------------------------------------------------------------------------------------------------------------
// MP COUNTDOWN - banner countdown bar (`mp-countdown` section)
// ---------------------------------------------------------------------------------------------------------------------
// Shows a Days/Hrs/Mins/Secs bar between `show` and `end` datetimes (both interpreted in America/Los_Angeles). Hides the
// days slot once it hits zero, removes itself when the countdown ends, and remembers a manual close for the session.
var MPCountdown = class extends HTMLElement {
  constructor() {
    super();
    if (window.sessionStorage.getItem('mpCountdownClosed') === 'true') {
      this.remove();
      return;
    }
    this._interval = null;
    this._end = this.getAttribute("end") ? DateTime.fromFormat(this.getAttribute("end") + " America/Los_Angeles", 'yyyy-MM-dd HH:mm:ss z').toJSDate() : null;
    this._show = this.getAttribute("show") ? DateTime.fromFormat(this.getAttribute("show") + " America/Los_Angeles", 'yyyy-MM-dd HH:mm:ss z').toJSDate() : null;

    this._close = this.querySelector('.close-btn');
    this._close.addEventListener("click", this._onCloseClicked.bind(this));

    this._days = this.querySelector('.days');
    this._daysSem = this.querySelector('.days-sem');
    this._hours = this.querySelector('.hours');
    this._minutes = this.querySelector('.minutes');
    this._seconds = this.querySelector('.seconds');
  }
  connectedCallback() {
    if (!this._isValidDate(this._end)) {
      this.disconnectedCallback();
      return;
    }

    if (this._isValidDate(this._show) && this._now() < this._show) {
      this.disconnectedCallback();
      return;
    }
    this._interval = setInterval(this._update.bind(this), 1e3);
    this._update();
  }
  _onCloseClicked() {
    this.style.height = '0';
    this.addEventListener('transitionend', function() {
      this.disconnectedCallback();
      window.sessionStorage.setItem('mpCountdownClosed', 'true');
    }, { once: true });
  }
  _now() {
    return DateTime.now().setZone('America/Los_Angeles').toJSDate();
  }
  _update() {
    const now = this._now();
    if (now >= this._end) {
      this.disconnectedCallback();
    } else {
      const [days, hours, minutes, seconds] = this._formatDiff(this._end - now);
      if (days === '00') {
        this._days.parentElement.style.display = 'none';
        this._daysSem.style.display = 'none';
      } else {
        this._days.innerHTML = days;
      }
      this._hours.innerHTML = hours;
      this._minutes.innerHTML = minutes;
      this._seconds.innerHTML = seconds;
    }
  }
  _isValidDate(d) {
    return d instanceof Date && !isNaN(d.getTime());
  }
  _formatDiff(diff) {
    const formatNumber = (num) => {
      return num < 10 ? `0${num}` : num;
    };
    const days = formatNumber(Math.floor(diff / (1000 * 60 * 60 * 24)));
    const hours = formatNumber(Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    const minutes = formatNumber(Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)));
    const seconds = formatNumber(Math.floor((diff % (1000 * 60)) / 1000));
    return [days, hours, minutes, seconds];
  }
  disconnectedCallback() {
    this._interval && clearInterval(this._interval);
    this.remove();
  }
};

if (!window.customElements.get("mp-countdown")) {
  window.customElements.define("mp-countdown", MPCountdown);
}

// ---------------------------------------------------------------------------------------------------------------------
// MP PRODUCT CARD - "BUY NOW" GOES STRAIGHT TO CHECKOUT
// ---------------------------------------------------------------------------------------------------------------------
// The card's "Buy now" and cart-icon buttons are both type=submit inside the theme's <form is="product-form">, which
// always does an AJAX add-to-cart (drawer / cart page). For "Buy now" we instead add the item then redirect to the
// checkout. The cart-icon button keeps the theme's default behaviour. Delegated + capture so it pre-empts product-form.
document.addEventListener("submit", async (event) => {
  const submitter = event.submitter;
  if (!submitter || !submitter.classList.contains("mp-product-card__buttons-buy-now")) {
    return;
  }

  const form = event.target;
  event.preventDefault();
  event.stopPropagation(); // prevent the theme's product-form add-to-cart handler from also running

  if (submitter.hasAttribute("aria-busy")) {
    return;
  }
  submitter.setAttribute("aria-busy", "true");

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";

  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      body: new FormData(form)
    });

    if (response.ok) {
      window.location.href = `${root}checkout`;
      return;
    }

    const error = await response.json();
    form.dispatchEvent(new CustomEvent("cart:error", { bubbles: true, detail: { error: error.description } }));
  } catch (e) {
    // network error - re-enable the button below so the customer can retry
  }

  submitter.removeAttribute("aria-busy");
}, true);
